<script setup lang="ts">
import type { Article } from '~/types/article'

withDefaults(
  defineProps<{
    article: Article
    featured?: boolean
  }>(),
  { featured: false },
)
</script>

<template>
  <article
    v-if="article._path && article.title"
    class="flex flex-col gap-4 md:gap-4"
    :class="featured ? 'md:flex-row md:gap-8' : ''"
  >
    <div
      v-if="article.cover"
      class="relative flex-1"
    >
      <div
        v-if="article?.badges?.length"
        class="absolute z-10 flex flex-wrap gap-2 mt-2 ml-2"
      >
        <span
          v-for="(badge, index) in article.badges"
          :key="index"
          class="px-2 py-0.5 rounded text-xs font-bold"
          :style="{
            backgroundColor: badge?.bg || 'rgba(0, 0, 0, 0.3)',
            color: badge?.color || 'white',
          }"
        >
          {{ typeof badge === 'string' ? badge : badge.content }}
        </span>
      </div>

      <div
        v-if="article?.tags?.length"
        class="absolute z-10 top-2 left-2"
      >
        <TagsList :tags="article.tags" />
      </div>

      <NuxtLink :to="article._path">
        <NuxtImg
          :src="article.cover"
          :alt="article.title"
          width="800"
          height="450"
          class="w-full aspect-video object-cover rounded-lg"
        />
      </NuxtLink>
    </div>

    <div class="flex flex-col flex-1">
      <NuxtLink
        :to="article._path"
        class="font-semibold text-gray-900 dark:text-white hover:text-primary-500 line-clamp-2 transition-colors"
        :class="featured ? 'text-4xl line-clamp-3 md:text-4xl' : 'text-2xl'"
      >
        <h2 class="m-0 p-0 text-inherit font-inherit leading-inherit">
          {{ article.title }}
        </h2>
      </NuxtLink>

      <p
        class="my-2 text-gray-600 dark:text-gray-400 line-clamp-2"
        :class="featured ? 'line-clamp-4 md:line-clamp-4' : ''"
      >
        {{ article.description }}
      </p>
      <time class="text-sm text-gray-500 dark:text-gray-500">
        {{ formatDate(article.date) }}
      </time>
    </div>
  </article>
</template>
